// AssignProsecutorModal.jsx
import UserSelect from "components/form/inputs/user_select";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getEmployeesByAdminAndOrg } from "services/getEmployees";
import { createResearchTeam, updateLegalResearchRequest, updateLegalResearchRequestCommittee } from "services/legalResearchService";
import { createProsecutorAssignment } from "services/prosecutorAssignentService";
import { LoadingIcon } from "utils/constant";
import { LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";

interface AssignProsecutorModalProps {
    data: any;
    setLegalResearch: Function;
    isOpen: boolean;
    onClose: () => void;
}

const AssignProsecutorModal: React.FC<AssignProsecutorModalProps> = ({
    data: singleData,
    setLegalResearch,
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation();
    const [submitting, setSubmitting] = useState(false);
    const { showToast } = useToast();
    const [employees, setEmployees] = useState([]);
    const contextData = useAuth();

    useEffect(() => {
        getEmployeesByAdminAndOrg({
            bearer:
                contextData.token ??
                contextData?.user?.access_token ??
                contextData?.user.user?.access_token,
            org_unit: contextData?.user?.org_unit || "",
            admin_unit: contextData?.user?.admin_unit || "",
        })
            .then((data) => {
                setEmployees(
                    data
                        .filter((emp: any) =>
                            emp.roles?.some(
                                (role: string) => role === "LegalResearchProsecutor"
                            )
                        )
                        .map((emp: any) => ({
                            id: emp.user.id,
                            name: emp.user.first_name,
                            role: emp.roles[0] || "Prosecutor",
                        }))
                );
            })
            .catch((error) => {
                console.error("Error fetching:", error);
                showToast(t("FailedToFetchEmployees"), "error");
            });
    }, [getEmployeesByAdminAndOrg, setEmployees]);

    const [formState, setFormState] = useState({
        remark: "",
        selectedDepartment: "",
        selectedProsecutor: null as any,
    });

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setFormState((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const { remark, selectedProsecutor } = formState;

    if (!isOpen) return null;

    const handleAssignSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);

        console.log(selectedProsecutor);
        const teamData = new FormData();
        teamData.append("team_name", `Committee Team ${singleData.id}`);
        teamData.append("research_request", singleData.id);

        try {
            const teamResponse = await createResearchTeam(contextData.token, teamData);

            const prosecutorData = new FormData();
            prosecutorData.append("research_request", singleData.id.toString());
            prosecutorData.append("prosecutor_name", selectedProsecutor.name);
            prosecutorData.append("prosecutor_user_id", selectedProsecutor.id.toString());
            prosecutorData.append("team_type", "prosecutor_topic_team");
            prosecutorData.append("is_team_leader", "true");
            prosecutorData.append("research_team", teamResponse.id);

            await createProsecutorAssignment(prosecutorData);

            const data = new FormData();
            data.append("remark_for_prosecutor", formState.remark);
            data.append(
                "assigned_by",
                contextData.user?.user?.stack_holder_name ??
                contextData.user?.full_name ??
                ""
            );
            data.append("assigned_prosecutor_id", selectedProsecutor.id);
            data.append("assigned_prosecutor_name", selectedProsecutor.name);
            data.append("status", LEGAL_RESEARCH_REQUEST_STATUS.research_underway.value);
            data.append("forward_status", LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value);

            const response = await updateLegalResearchRequest(singleData.id, data, contextData.token);

            setLegalResearch((prev: any) => ({
                ...prev,
                ...response,
            }));

            showToast(t("ProsecutorAssignedSuccess"), "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToAssignProsecutor"), "error");
        } finally {
            setSubmitting(false);
        }

        onClose();
    };

    const handleUserSelect = (selectedUser: any) => {
        setFormState((prev) => ({ ...prev, selectedProsecutor: selectedUser }));
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <form
                onSubmit={handleAssignSubmit}
                className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6 relative"
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {t("AssignProsecutor")}
                </h2>

                {/* Remark Section */}
                <div className="mb-4">
                    <label
                        htmlFor="remark"
                        className="block text-sm font-medium text-gray-700 mb-2"
                    >
                        {t("Remark")}
                    </label>
                    <textarea
                        id="remark"
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                        placeholder={t("DetailHere")}
                        value={remark}
                        name="remark"
                        onChange={handleChange}
                    ></textarea>
                </div>

                <label
                    htmlFor="select-department"
                    className="block text-sm font-medium text-gray-700 mb-2"
                >
                    {t("SelectProsecutor")}
                </label>
                <UserSelect required options={employees} onSelect={handleUserSelect} />

                {/* Assign Button */}
                <div className="text-center mt-6">
                    <button
                        type="submit"
                        className="bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-8 rounded-md w-full"
                    >
                        {submitting ? (
                            <div className="flex items-center justify-center">
                                {LoadingIcon} {t("Submitting")}
                            </div>
                        ) : (
                            t("Assign")
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AssignProsecutorModal;